// @ts-check

import { atom, useAtomValue } from "jotai";
import { useEffect } from "react";

/**
 * @typedef {Object} SharedEffectsMapEntry
 * @property {import("react").DependencyList | null} deps
 * @property {ReturnType<import("react").EffectCallback>} [cleanup]
 * @property {number} subscribersCount
 */

/**
 * @param {import("react").DependencyList | null} currentDeps 
 * @param {import("react").DependencyList | null} newDeps 
 */
function areDepsEqual(currentDeps, newDeps) {
  if (currentDeps === null || newDeps === null) return false
  if (currentDeps.length !== newDeps.length) return false

  for (let i = 0; i < currentDeps.length; i++) {
    if(Object.is(currentDeps[i], newDeps[i])) continue
    return false
  }

  return true
}

const effectsMapAtom = atom(/** @type {Map<symbol, SharedEffectsMapEntry>} */(new Map()));

/** @param {SharedEffectsMapEntry} entry */
function cleanup(entry) {
  if (typeof entry.cleanup === "function") {
    entry.cleanup();
    entry.cleanup = null;
    entry.deps = null;
  }
}

/**
 * @param {Map<symbol, SharedEffectsMapEntry>} effectsMap
 * @param {SharedEffectsMapEntry} entry
 * @param {import("react").EffectCallback} effect
 * @param {import("react").DependencyList} [deps]
 */
function runEffect(effectsMap, entry, effect, deps) {
  if (areDepsEqual(entry.deps, deps)) return;

  cleanup(entry);
  entry.deps = deps;

  const cleanupFn = effect();
  entry.cleanup = cleanupFn;
}

/**
 * @param {Map<symbol, SharedEffectsMapEntry>} effectsMap
 * @param {symbol} key
 * @param {import("react").EffectCallback} effect
 * @param {import("react").DependencyList} [deps]
 */
function register(effectsMap, key, effect, deps) {
  let entry = effectsMap.get(key);
  if (entry == null) {
    entry = { deps: null, subscribersCount: 0 };
    effectsMap.set(key, entry);
  }
  entry.subscribersCount++;

  runEffect(effectsMap, entry, effect, deps);
  return () => unregister(effectsMap, key);
}

/**
 * @param {Map<symbol, SharedEffectsMapEntry>} effectsMap
 * @param {SharedEffectsMapEntry} entry
 */
function dispose(effectsMap, entry) {
  if (entry.subscribersCount > 0) return;
  cleanup(entry);
}

/**
 * @param {Map<symbol, SharedEffectsMapEntry>} effectsMap
 * @param {symbol} key
 */
function unregister(effectsMap, key) {
  let entry = effectsMap.get(key);
  if (entry == null) {
    throw new Error("Cannot unregister unregistered shared effect");
  }
  entry.subscribersCount--;

  dispose(effectsMap, entry);
}

/**
 * Creates an effect that executes once for changed dependencies for each key
 * even if it is instantiated multiple times.
 * 
 * Current implementation assumes that;
 * - a single effect is registered per key
 * - all dependencies are from shared global state
 * - it doesn't matter which instance of the effect is executed
 * 
 * @param {symbol} key
 * @param {import("react").EffectCallback} effect
 * @param {import("react").DependencyList} [deps]
 */
function useSharedEffect(key, effect, deps) {
  const effectsMap = useAtomValue(effectsMapAtom)

  return useEffect(() => {
    return register(effectsMap, key, effect, deps)
  }, [effectsMap, key, ...deps])
}


export default useSharedEffect